import { LazyMotion, domAnimation, m } from 'framer-motion';
import { useTheme, alpha } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import { Icon } from '@iconify/react';
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// ---------------------------------------------------

const THOUGHTS = [
  'Extracting information',
  'Analyzing details',
  'Creating project',
  'Finalizing everything',
];

// ---------------------------------------------------

export default function LoaderDialog({ dialog, onClose }) {
  const theme = useTheme();
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!dialog.value) return;

    const timer = setInterval(() => {
      if (step === THOUGHTS.length - 1) {
        clearInterval(timer);
        onClose();
        return;
      }
      setStep((prev) => (prev + 1) % THOUGHTS.length);
    }, 2000);
  }, [dialog.value, onClose, step]);

  const primary = theme.palette.primary.main;
  const primaryLight = theme.palette.primary.light;
  const primaryDark = theme.palette.primary.dark;
  const grey = theme.palette.grey[500];

  return (
    <Dialog
      open={dialog.value}
      onClose={(_, reason) => {
        // Optional: prevent closing while loading
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        dialog.onFalse();
      }}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          backgroundColor: theme.palette.background.default,
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: alpha(theme.palette.grey[900], 0.6),
          backdropFilter: 'blur(4px)',
        },
      }}
    >
      <LazyMotion features={domAnimation}>
        <Stack
          height={420}
          alignItems="center"
          justifyContent="center"
          spacing={4}
          sx={{
            position: 'relative',
            overflow: 'hidden',
            px: 4,
          }}
        >
          {/* -------- Background Grid -------- */}
          <Box
            component={m.div}
            animate={{ opacity: [0.04, 0.1, 0.04] }}
            transition={{ repeat: Infinity, duration: 6, ease: 'easeInOut' }}
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `
                linear-gradient(${alpha(grey, 0.15)} 1px, transparent 1px),
                linear-gradient(90deg, ${alpha(grey, 0.15)} 1px, transparent 1px)
              `,
              backgroundSize: '48px 48px',
              pointerEvents: 'none',
            }}
          />

          {/* -------- Core AI Orb -------- */}
          <Box
            component={m.div}
            animate={{
              scale: [1, 1.08, 1],
              boxShadow: [
                `0 0 18px ${alpha(primary, 0.35)}`,
                `0 0 42px ${alpha(primary, 0.65)}`,
                `0 0 18px ${alpha(primary, 0.35)}`,
              ],
            }}
            transition={{ repeat: Infinity, duration: 2.6, ease: 'easeInOut' }}
            sx={{
              width: 112,
              height: 112,
              borderRadius: '50%',
              background: `radial-gradient(circle at 30% 30%, ${primaryLight}, ${primaryDark})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
            }}
          >
            <Icon
              icon="solar:cpu-bold-duotone"
              width={48}
              height={48}
              color={theme.palette.common.white}
            />
          </Box>

          {/* -------- Orbit Rings -------- */}
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              component={m.div}
              animate={{ rotate: 360 }}
              transition={{
                repeat: Infinity,
                duration: 8 + i * 3,
                ease: 'linear',
              }}
              sx={{
                position: 'absolute',
                width: 220 + i * 48,
                height: 220 + i * 48,
                borderRadius: '50%',
                border: `1px dashed ${alpha(primary, 0.25)}`,
              }}
            />
          ))}

          {/* -------- Thought Text -------- */}
          <Typography
            component={m.div}
            key={step}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            fontSize="1.15rem"
            fontWeight={600}
            sx={{
              textAlign: 'center',
              background: `linear-gradient(90deg, ${primary}, ${primaryDark})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {THOUGHTS[step]}
          </Typography>

          {/* -------- Supporting Copy -------- */}
          <Typography
            fontSize="0.9rem"
            color="text.secondary"
            sx={{ maxWidth: 360, textAlign: 'center' }}
          >
            Our AI is extracting the information from the PDF and preparing your project.
          </Typography>
        </Stack>
      </LazyMotion>
    </Dialog>
  );
}

LoaderDialog.propTypes = {
  dialog: PropTypes.shape({
    value: PropTypes.bool,
    onFalse: PropTypes.func,
  }),
  onClose: PropTypes.func,
};
